import { ANY_EVENT, type EventBus, type LabEvent, type Unsubscribe } from "../core/event-bus.js";
import { EVENT_CATALOG } from "./event-catalog.js";
import { buildEnvelope, type EnvelopeEvent, type EventEnvelope } from "./envelope.js";
import { computeBackoffMs, OfflineQueue } from "./offline-queue.js";

export interface Reporter {
  send(envelope: EventEnvelope): Promise<{ ok: boolean }>;
}

/** 默认 reporter：不出网，只用于离线/未配置后端时不报错。 */
export class NullReporter implements Reporter {
  async send(): Promise<{ ok: boolean }> {
    return { ok: true };
  }
}

export interface TelemetryClientOptions {
  instanceId: string;
  sessionId: string;
  experimentId: string;
  experimentVersionId: string;
  reporter?: Reporter;
  batchWindowMs?: number;
  batchMaxSize?: number;
}

export interface RecordResult {
  accepted: boolean;
  reason?: string;
}

const DEFAULT_BATCH_WINDOW_MS = 3000;
const DEFAULT_BATCH_MAX_SIZE = 20;

/**
 * 遥测客户端：分级过滤 + 批量窗口 + critical 直发 + 离线队列。对应 tech/108 §8。
 *
 * 生产路径只应通过 `start(bus)` 订阅事件总线（遥测层只订阅，不反向调用交互/逻辑层）；
 * `recordDirect` 是给单测用的低层入口，用来验证"未注册事件类型被拒绝""缺 correct 被拒绝"这类
 * 目录白名单行为，不应被 mission-engine/controller 直接调用。
 */
export class TelemetryClient {
  private readonly queue: EnvelopeEvent[] = [];
  private readonly offlineQueue = new OfflineQueue();
  private readonly reporter: Reporter;
  private flushTimer: ReturnType<typeof setTimeout> | undefined;
  private retryAttempt = 0;

  constructor(private readonly options: TelemetryClientOptions) {
    this.reporter = options.reporter ?? new NullReporter();
  }

  start(bus: EventBus): Unsubscribe {
    return bus.on(ANY_EVENT, (event: LabEvent) => {
      const payload = event.payload as Record<string, unknown>;
      this.recordDirect(event.type, payload, {
        stepId: typeof payload.stepId === "string" ? payload.stepId : undefined,
        seq: event.seq,
        ts: event.ts,
      });
    });
  }

  recordDirect(
    type: string,
    payload: Record<string, unknown>,
    meta: { stepId?: string; seq?: number; ts?: number } = {},
  ): RecordResult {
    const entry = EVENT_CATALOG[type];
    if (!entry) {
      return { accepted: false, reason: "unknown_event_type" };
    }
    if (entry.judgmentPoint && !("correct" in payload)) {
      return { accepted: false, reason: "missing_correct_field" };
    }
    if (entry.level === 3) {
      // 本次 P0 无 L3 事件产生场景；按分级约定即使注册了 L3 也不出网、不进离线队列。
      return { accepted: true };
    }

    const record: EnvelopeEvent = {
      eventId: `evt_${meta.seq ?? this.queue.length}_${meta.ts ?? Date.now()}`,
      sequence: meta.seq ?? this.queue.length,
      eventType: type,
      level: entry.level,
      stepId: meta.stepId,
      clientTime: new Date(meta.ts ?? Date.now()).toISOString(),
      payload,
    };

    this.queue.push(record);
    if (entry.critical) {
      void this.flush();
    } else {
      this.scheduleFlush();
    }
    return { accepted: true };
  }

  private scheduleFlush(): void {
    const maxSize = this.options.batchMaxSize ?? DEFAULT_BATCH_MAX_SIZE;
    if (this.queue.length >= maxSize) {
      void this.flush();
      return;
    }
    if (this.flushTimer) return;
    const window = this.options.batchWindowMs ?? DEFAULT_BATCH_WINDOW_MS;
    this.flushTimer = setTimeout(() => void this.flush(), window);
  }

  async flush(): Promise<void> {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = undefined;
    }
    if (this.queue.length === 0) return;
    const events = this.queue.splice(0, this.queue.length);
    await this.send(events);
  }

  private async send(events: EnvelopeEvent[]): Promise<void> {
    const envelope = buildEnvelope(
      this.options.instanceId,
      this.options.sessionId,
      this.options.experimentId,
      this.options.experimentVersionId,
      events,
    );
    try {
      const result = await this.reporter.send(envelope);
      if (!result.ok) throw new Error("reporter rejected envelope");
      this.retryAttempt = 0;
      if (this.offlineQueue.size() > 0) {
        void this.retryOffline();
      }
    } catch {
      this.offlineQueue.enqueue(events);
      this.retryAttempt += 1;
      setTimeout(() => void this.retryOffline(), computeBackoffMs(this.retryAttempt));
    }
  }

  private async retryOffline(): Promise<void> {
    const pending = this.offlineQueue.drain();
    if (pending.length === 0) return;
    await this.send(pending);
  }

  offlineQueueSize(): number {
    return this.offlineQueue.size();
  }
}
